import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Grid, Stack, Button,
  TextField, MenuItem, FormControl, InputLabel, Select, InputAdornment,
  CircularProgress, LinearProgress, Paper, Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  EventBusy as AbsentIcon,
  EventAvailable as PresentIcon,
  BeachAccess as LeaveIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  useCycleAttendance,
  useDownloadMonthlyAttendanceExport,
  useDownloadAttendanceExport,
} from '../../hooks/useAttendance';
import { getCycleForPayrollMonth } from '../../utils/attendanceCycle';
import EmployeeAttendanceCalendar from './EmployeeAttendanceCalendar';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';

// ---- FORTHEYE soft-card tokens (shared with the Leave & Attendance dashboards) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff'; // primary blue
const GRADIENT_BTN = {
  textTransform: 'none', fontWeight: 700, borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
// Soft "borderless" select styling used inside the filter card.
const SELECT_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MonthlyAttendanceGrid = ({ teamOnly = false }) => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [department, setDepartment] = useState('');
  const [teamHead, setTeamHead] = useState('all');
  const [manager, setManager] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [reportMode, setReportMode] = useState('summary'); // 'summary' | 'daily'
  const [calendarEmp, setCalendarEmp] = useState(null);

  // Monthly Overview follows the 26→25 pay cycle: the Month/Year picker selects
  // the PAYROLL month, and we show the cycle paid in that month (e.g. June 2026
  // → 26 May – 25 Jun), so the numbers match the Cycle tab.
  const cycle = useMemo(() => getCycleForPayrollMonth(month, year), [month, year]);

  const params = useMemo(
    () => ({
      cycle: cycle.cycle,
      department: department || undefined,
      team_head: teamHead !== 'all' ? teamHead : undefined,
      manager: manager !== 'all' ? manager : undefined,
      search: search || undefined,
      team_scope: teamOnly ? 1 : undefined,
    }),
    [cycle, department, teamHead, manager, search, teamOnly],
  );

  const { data, isLoading, isFetching } = useCycleAttendance(params);
  const { mutate: downloadReport, isPending: isDownloadingSummary } = useDownloadMonthlyAttendanceExport();
  const { mutate: downloadDaily, isPending: isDownloadingDaily } = useDownloadAttendanceExport();
  const isDownloading = isDownloadingSummary || isDownloadingDaily;

  const handleDownload = () => {
    if (reportMode === 'daily') {
      // Day-wise sheet over the 26→25 cycle range (matches what's shown on screen).
      downloadDaily({
        startDate: cycle.startISO,
        endDate: cycle.endISO,
        department: department || undefined,
        teamHead, manager, search: search || undefined,
      });
      return;
    }
    // Summary export over the 26→25 cycle (the `cycle` param makes the backend
    // use the cycle range; month/year are sent only as a fallback).
    downloadReport({
      cycle: cycle.cycle,
      month, year, department: department || undefined, teamHead, manager, search: search || undefined,
    });
  };
  const teamHeads = data?.team_heads || [];
  const managers = data?.managers || [];

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const copy = [...data.rows];
    if (sortBy === 'attendance') copy.sort((a, b) => b.attendance_pct - a.attendance_pct);
    else if (sortBy === 'absent') copy.sort((a, b) => (b.totals?.absent || 0) - (a.totals?.absent || 0));
    else copy.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return copy;
  }, [data, sortBy]);

  const departments = useMemo(() => {
    const set = new Set();
    (data?.rows || []).forEach((r) => r.department && set.add(r.department));
    return Array.from(set).sort();
  }, [data]);

  const years = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  return (
    <Box>
      {/* Filters card */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <FormControl size="small" fullWidth sx={SELECT_SX}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                {MONTH_NAMES.map((name, i) => (
                  <MenuItem key={i} value={i + 1}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl size="small" fullWidth sx={SELECT_SX}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SELECT_SX}>
              <InputLabel>Department</InputLabel>
              <Select
                value={department}
                label="Department"
                onChange={(e) => setDepartment(e.target.value)}
                MenuProps={SELECTED_MENU_PROPS}
              >
                <MenuItem value="">All</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SELECT_SX}>
              <InputLabel>Team Head</InputLabel>
              <Select value={teamHead} label="Team Head" onChange={(e) => setTeamHead(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="all">All Team Heads</MenuItem>
                {teamHeads.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {!teamOnly && <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SELECT_SX}>
              <InputLabel>Manager</InputLabel>
              <Select value={manager} label="Manager" onChange={(e) => setManager(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="all">All Managers</MenuItem>
                {managers.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>}
          <Grid item xs={12} sm={8} md={2}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search name or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <FormControl size="small" fullWidth sx={SELECT_SX}>
              <InputLabel>Sort by</InputLabel>
              <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="name">Name (A→Z)</MenuItem>
                <MenuItem value="attendance">Attendance % (high→low)</MenuItem>
                <MenuItem value="absent">Absent days (high→low)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {!teamOnly && <Grid item xs={6} sm={4} md={2}>
            <FormControl size="small" fullWidth sx={SELECT_SX}>
              <InputLabel>Report</InputLabel>
              <Select value={reportMode} label="Report" onChange={(e) => setReportMode(e.target.value)} MenuProps={SELECTED_MENU_PROPS}>
                <MenuItem value="summary">Monthly summary</MenuItem>
                <MenuItem value="daily">Day-wise detail</MenuItem>
              </Select>
            </FormControl>
          </Grid>}
          {!teamOnly && <Grid item xs={6} sm={4} md={1.5}>
            <Button
              variant="contained"
              disableElevation
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={isDownloading || !(data?.rows?.length)}
              sx={{ ...GRADIENT_BTN, py: 1 }}
            >
              {isDownloading ? '…' : 'Download'}
            </Button>
          </Grid>}
        </Grid>
        {isFetching && (
          <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />
        )}
      </Paper>

      {/* Total overview card */}
      <OverviewCard
        cycle={cycle}
        overview={data?.overview}
        employeeCount={sortedRows.length}
      />

      {/* Employee cards grid */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      ) : sortedRows.length === 0 ? (
        <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 6 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CalendarIcon />
          </Box>
          <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>
            No employees match your filters.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {sortedRows.map((row) => (
            <Grid item xs={12} sm={6} md={4} key={row.employee_no}>
              <EmployeeCard
                row={row}
                onOpenCalendar={() => setCalendarEmp(row.employee_no)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {calendarEmp && (
        <EmployeeAttendanceCalendar
          open
          onClose={() => setCalendarEmp(null)}
          employeeNo={calendarEmp}
          mode="cycle"
          cycle={cycle.cycle}
          initialViewMode="month"
          initialMonth={month}
          initialYear={year}
        />
      )}
    </Box>
  );
};

const OverviewCard = ({ cycle, overview, employeeCount }) => (
  <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2}>
      <Box>
        <Typography sx={{ color: '#6b7280', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Attendance Cycle (26–25)
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', md: '1.9rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
          {cycle.label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600, mt: 0.5 }}>
          Payroll: {cycle.payrollMonthName} {cycle.payrollYear} · {employeeCount} employee{employeeCount === 1 ? '' : 's'}
        </Typography>
      </Box>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <OverviewStat icon={<PresentIcon />} label="Present" value={overview?.present_total ?? 0} color="#16a34a" />
        <OverviewStat icon={<AbsentIcon />}  label="Absent"  value={overview?.absent_total ?? 0} color="#ef4444" />
        <OverviewStat icon={<LeaveIcon />}   label="Leaves"  value={overview?.leave_total ?? 0} color="#ef4444" />
        <OverviewStat icon={<CalendarIcon />} label="Holidays" value={overview?.holiday_total ?? 0} color="#cbd5e1" />
        <OverviewStat icon={<TrendingUpIcon />} label="Not recorded" value={overview?.not_recorded_total ?? 0} color="#9ca3af" />
      </Stack>
    </Stack>
  </Paper>
);

const OverviewStat = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center', minWidth: 90 }}>
    <Stack direction="row" alignItems="center" spacing={0.75} justifyContent="center">
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#111827', lineHeight: 1.1 }}>{value}</Typography>
    </Stack>
    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>{label}</Typography>
  </Box>
);

const EmployeeCard = ({ row, onOpenCalendar }) => {
  const pct = row.attendance_pct ?? 0;
  const pctColor = pct >= 90 ? '#16a34a' : pct >= 75 ? '#f59e0b' : '#ef4444';
  const initials = (row.name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <Paper
      elevation={0}
      sx={{
        ...CARD_SX,
        height: '100%',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 12px 28px rgba(17,24,39,0.08)',
        },
        cursor: 'pointer',
      }}
      onClick={onOpenCalendar}
    >
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: `${ACCENT}14`, color: ACCENT, fontWeight: 800 }}>
          {initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: '#111827' }} noWrap>
            {row.name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }} noWrap>
            {row.employee_no} · {row.designation || '—'}
          </Typography>
        </Box>
      </Stack>

      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 2 }}>
        {row.department || '—'} · {row.reporting_manager || row.team_head || '—'}
      </Typography>

      <Box
        sx={{
          p: 2,
          borderRadius: '14px',
          bgcolor: '#f8fafc',
          border: '1px solid #eef0f4',
          mb: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1.5}>
          <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Attendance
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: pctColor }}>
            {pct}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: `${pctColor}1f`,
            '& .MuiLinearProgress-bar': { bgcolor: pctColor, borderRadius: 4 },
          }}
        />
        <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
          <MiniStat label="P" value={row.totals?.present || 0} fg="#16a34a" bg="#f0fdf4" />
          <MiniStat label="A" value={row.totals?.absent || 0} fg="#ef4444" bg="#fef2f2" />
          <MiniStat label="L" value={row.totals?.leave || 0} fg="#ef4444" bg="#fef2f2" />
          <MiniStat label="H" value={row.totals?.holiday || 0} fg="#64748b" bg="#f1f5f9" />
          <MiniStat label="—" value={row.totals?.not_recorded || 0} fg="#6b7280" bg="#f3f4f6" />
        </Stack>
      </Box>

      <Button
        fullWidth
        variant="outlined"
        startIcon={<ViewIcon />}
        onClick={(e) => { e.stopPropagation(); onOpenCalendar(); }}
        sx={{
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 700,
          borderColor: `${ACCENT}55`,
          color: ACCENT,
          '&:hover': { borderColor: ACCENT, bgcolor: `${ACCENT}0d` },
        }}
      >
        View Calendar
      </Button>
    </Paper>
  );
};

const MiniStat = ({ label, value, fg, bg }) => (
  <Box
    sx={{
      px: 1,
      height: 22,
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '8px',
      bgcolor: bg,
      color: fg,
      fontWeight: 700,
      fontSize: 11,
    }}
  >
    {`${label} ${value}`}
  </Box>
);

export default MonthlyAttendanceGrid;
